<template>
  <article>
    <CollectionPageHeader :name="collection.name" :image-url="collection.image_url">
      <template #description>
        <MarkdownText
          class="mb-5 text-text-lg leading-[1.4] md:text-text-2xl md:leading-8"
          :text="collection.desc"
          @clicked:link="trackLinkClick"
        />
      </template>
    </CollectionPageHeader>

    <div class="bg-gray-50 pb-[84px] pt-[42px]">
      <div :class="BOOTSTRAP_CONTAINER">
        <div class="md:-mx-[15px] md:flex">
          <div class="hidden md:sticky md:top-5 md:block md:max-h-[calc(100vh-20px)] md:w-1/3 md:self-start md:overflow-y-auto md:px-[15px]">
            <CollectionTableOfContents :entries="tocEntries" />
          </div>
          <div data-testid="collection-content" class="md:w-2/3 md:px-[15px]" :class="{ '-mt-[84px]': startsWithSection }">
            <div v-for="(item, index) in collection.content" :key="item.content_uuid">
              <CollectionSectionHeading
                v-if="item.content_type === CONTENT_TYPES.TEXT_HEADER"
                :id="item.content_uuid"
                :text="item.text"
              />
              <CollectionParagraph v-else-if="item.content_type === CONTENT_TYPES.TEXT_RICH_TEXT" :id="item.content_uuid">
                <MarkdownText :text="item.text" @clicked:link="trackLinkClick" />
              </CollectionParagraph>
              <CollectionContentCard
                v-else-if="item.content_type === CONTENT_TYPES.STRATEGY && item.content_body"
                label="Strategy Page"
                :image-url="item.content_thumbnail_url"
                :title="item.content_title"
                :heading-level="cardHeadingLevel(index)"
              >
                <template #description>
                  <MarkdownText :text="item.text" @clicked:link="trackLinkClick" />
                </template>
                <template #bar>
                  <a
                    :href="keepingBetaOptIn(toolkitUrls.strategyPage(item.content_body.slug))"
                    target="_blank"
                    class="inline-block select-none rounded-xs border border-blue-400 bg-blue-400 px-3 py-1.5 text-text-md leading-5 text-white hover:border-blue-500 hover:bg-blue-500 focus-visible:outline-offset-2"
                    @click="trackStrategyClick(item.content_body)"
                  >View Strategy</a>
                  <span
                    v-if="item.content_body.resource_count > 0"
                    data-testid="strategy-resource-count"
                    class="ml-4 text-gray-700"
                  ><span class="select-none"><span class="material-icons-round align-middle text-text-lg" aria-hidden="true">download</span><span class="sr-only">Downloadable resources: </span></span>{{ item.content_body.resource_count }}</span>
                </template>
              </CollectionContentCard>
              <CollectionContentCard
                v-else-if="item.content_type === CONTENT_TYPES.RESOURCE"
                label="Printable Resource"
                :image-url="item.content_thumbnail_url"
                :title="item.content_title"
                :heading-level="cardHeadingLevel(index)"
              >
                <template #description>
                  <MarkdownText :text="item.text" @clicked:link="trackLinkClick" />
                </template>
                <template #bar>
                  <ResourceDownloadButton :resource="item.content_body" />
                </template>
              </CollectionContentCard>
            </div>
          </div>
        </div>
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import MarkdownText from '@/components/MarkdownText.vue'
import ResourceDownloadButton from '@/resources/ResourceDownloadButton.vue'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import { toolkitUrls } from '@/util/links'
import { keepingBetaOptIn } from '@/util/beta_opt_in'
import CollectionPageHeader from './CollectionPageHeader.vue'
import CollectionTableOfContents from './CollectionTableOfContents.vue'
import CollectionContentCard from './CollectionContentCard.vue'
import CollectionSectionHeading from './CollectionSectionHeading.vue'
import CollectionParagraph from './CollectionParagraph.vue'
import { BOOTSTRAP_CONTAINER } from './bootstrap_container'
import { CONTENT_TYPES, type CollectionDetailContent, type CollectionStrategy, type CuratedCollectionDetail } from './curated_collection_types'

const props = defineProps<{ collection: CuratedCollectionDetail }>()

const analytics = getAnalyticsTracker()

const isSectionHeading = (item: CollectionDetailContent) => item.content_type === CONTENT_TYPES.TEXT_HEADER

const tocEntries = computed(() =>
  props.collection.content
    .filter(isSectionHeading)
    .map((heading) => ({ text: heading.text, target: heading.content_uuid }))
)

const firstSectionIndex = computed(() => props.collection.content.findIndex(isSectionHeading))
const startsWithSection = computed(() => firstSectionIndex.value === 0)

function cardHeadingLevel(index: number) {
  return firstSectionIndex.value >= 0 && index > firstSectionIndex.value ? 3 : 2
}

function trackLinkClick({ text, url }: { text: string, url: string }) {
  analytics.sendEvent(EVENT_NAMES.CURATED_COLLECTION.CLICKED_EXTERNAL_LINK, { text, url })
}

function trackStrategyClick(strategy: CollectionStrategy) {
  analytics.sendEvent(EVENT_NAMES.CURATED_COLLECTION.CLICKED_STRATEGY, {
    strategy_uuid: strategy.uuid,
    strategy_title: strategy.title
  })
}
</script>
